import { useRouter } from '@main/core-ui/src/hooks/useRouter';
import { addQueryParams } from '@main/core-ui/src/utils/location';
import { indexTree } from '@main/utils';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import React, { useCallback, useEffect, useState } from 'react';

import { Tooltip } from '../Tooltip';
import { MenuItem, SubMenu } from './Menu';
import { SideMenuTab } from './SideMenuTab';
import { MenuTab, SideMenuProps, SideMenuTabProps } from './types';
import { SideMenuWrapper } from './wrappers';

/**
 * The default menu tab
 */
export interface DefaultMenuTab {
  /** Child tabs must be a list */
  children?: DefaultMenuTab[];
  /** Most have an id attribute */
  id: string;
}

/** Index from child id to all of its parent ids */
export type ParentLookup = { [k in string]: string[] };

/** Lookup menu tab by visual id */
export type MenuTabLookup = { [visualId in string]: MenuTab };

export type { MenuTab, SideMenuTabProps };

const OVERFLOWED_INDICATOR_KEY = 'overflowed-indicator';

/**
 * Display a side menu that can switch which components are being viewed
 */
export const SideMenu: React.FC<SideMenuProps> = (props) => {
  const {
    selected,
    tabs,
    mode = 'inline',
    onSelectChange,
    onOpenChange: onOpenChangeProp,
    accordion,
    alwaysDropdown,
    queryParam,
    fullUrl,
    haveGradient = false,
    MenuWrapper = SideMenuWrapper,
    maxHeight = '800px',
    minHeight = '',
    style = { overflowY: 'auto' },
    values,
    initialOpenKeys,
    initialSelectedKeys,
    ...restForDiv
  } = props;

  const { location, redirect } = useRouter();

  // Index from child ids to all of its parents
  const [parentLookup, setParentLookup] = useState<ParentLookup>({});

  // Needed in order to allow mobile side menu to re-expand URG
  const [resetInd, setResetInd] = useState(0);

  // Lookup menu item by visual id. Only indexing the lead nodes
  const [lookupMenuTab, setLookupMenuTab] = useState<MenuTabLookup>({});

  // The keys of all of all menu items in the tree
  const [allMenuKeys, setAllMenuKeys] = useState<string[]>([]);

  // Local state for selected keys
  const [localSelectedKeys, setLocalSelectedKeys] = useState<string[]>(
    initialSelectedKeys || [],
  );

  // Local state for open keys
  const [localOpenKeys, setLocalOpenKeys] = useState<string[]>(
    initialOpenKeys || [],
  );

  // Callback to alert parent of change - Memoize with useCallback
  const onChange = useCallback(
    (newSelected: string, newTab: boolean): void => {
      if (!onSelectChange) {
        return;
      }
      const selectedNode = lookupMenuTab[newSelected];
      const selectedParents = (parentLookup[newSelected] || []).map(
        (parentVisualId) => lookupMenuTab[parentVisualId],
      );
      onSelectChange({
        newMenu: newSelected,
        menuTab: selectedNode,
        parentTabs: selectedParents,
        newTab,
      });
    },
    [onSelectChange, lookupMenuTab, parentLookup],
  );

  // Whenever the tabs change, we create an index of the tree to display the tree
  useEffect(() => {
    const indexedTabs = indexTree(tabs, 'visualId');

    if (!isEqual(indexedTabs.parentLookup, parentLookup)) {
      setParentLookup(indexedTabs.parentLookup);
    }
    setLookupMenuTab(indexedTabs.lookupTreeTab);

    const newMenuKeys = indexedTabs.allMenuKeys;
    if (!isEqual(newMenuKeys, allMenuKeys)) {
      setAllMenuKeys(newMenuKeys);
    }

    // Set the open keys - Use initialOpenKeys or derive default if not provided
    if (initialOpenKeys === undefined) {
      let defaultKeys: string[] = [];
      if (selected && mode !== 'horizontal') {
        defaultKeys = parentLookup[selected] || [selected];
      } else if (mode !== 'horizontal') {
        // Grab the first item
        let [first] = tabs.filter(({ children }) => children);

        // Get inner most
        while (first && first.children && first.children.length > 0) {
          [first] = first.children;
        }

        // Set to default open key
        if (first) {
          defaultKeys = parentLookup[first.visualId] || [first.visualId];
        }
      }
      setLocalOpenKeys(alwaysDropdown ? newMenuKeys : defaultKeys);
    } else if (alwaysDropdown) {
      setLocalOpenKeys(newMenuKeys);
    }

    if (!isEqual(newMenuKeys, allMenuKeys)) {
      setResetInd((r) => r + 1);
    }
  }, [
    tabs,
    selected,
    mode,
    initialOpenKeys,
    alwaysDropdown,
    allMenuKeys,
    parentLookup,
  ]);

  // Sync localSelectedKeys with the selected prop (if provided and different)
  // AND Sync with initialSelectedKeys on mount/change
  useEffect(() => {
    // If initialSelectedKeys is provided, prioritize it
    if (initialSelectedKeys) {
      setLocalSelectedKeys(initialSelectedKeys);
    } else if (selected) {
      // Otherwise, use the selected prop if available
      setLocalSelectedKeys([selected]);
    } else {
      // Fallback to empty array if neither is provided
      setLocalSelectedKeys([]);
    }
  }, [selected, initialSelectedKeys]); // Depend on both

  // Handler for selecting an item
  const handleSelect = (key: string, metaKey: boolean): void => {
    // Update local state
    setLocalSelectedKeys([key]);

    // Get the full node
    const menuTab = lookupMenuTab[key];
    if (!menuTab && !fullUrl) {
      // Also check fullUrl existence
      // If no menuTab and no fullUrl, only call onChange if needed.
      if (!key.includes(OVERFLOWED_INDICATOR_KEY)) {
        onChange(key, metaKey);
      }
      return;
    }

    // Call original onChange handler (do this before navigation attempts)
    if (!key.includes(OVERFLOWED_INDICATOR_KEY)) {
      onChange(key, metaKey);
    }

    // Determine if the link is external
    const isExternalLink = menuTab?.href?.startsWith('http');

    // Handle navigation
    if (isExternalLink && menuTab?.href) {
      // External Link: Open in new tab (don't append queryParam from prop)
      window.open(menuTab.href, '_blank');
    } else if (menuTab?.href) {
      // Internal Navigation - Case 1: Tab has an href (likely a hash '#...')
      redirect(
        {
          ...location,
          pathname: fullUrl || location.pathname,
          hash: menuTab.href, // Update hash using href
        },
        metaKey,
      );
    } else if (queryParam !== undefined) {
      // Internal Navigation - Case 2: SideMenu has a queryParam prop like "paramKey=currentValue"
      // Extract the key part (e.g., "dataPointId")
      const paramKey = queryParam.split('=')[0];
      if (paramKey) {
        // Update the specific query parameter using the new item's key
        redirect(addQueryParams(location, { [paramKey]: key }), metaKey);
      }
    }
  };

  // Handler for toggling a submenu
  const handleToggleOpen = (key: string): void => {
    let newOpenKeys: string[];
    const currentlyOpen = localOpenKeys.includes(key);

    if (accordion) {
      // Accordion logic: If opening, close others and open clicked + its parents.
      // If closing, just remove it.
      if (!currentlyOpen) {
        newOpenKeys = uniq([key, ...(parentLookup[key] || [])]);
      } else {
        // Simple close: remove the key
        newOpenKeys = localOpenKeys.filter((k) => k !== key);
        // TODO: Consider if children should also be closed explicitly?
        // https://transcend.height.app/T-44677
      }
    } else {
      // Normal logic: Add or remove the key. Refactor to avoid lone if in else.
      newOpenKeys = currentlyOpen
        ? localOpenKeys.filter((k) => k !== key)
        : [...localOpenKeys, key];
    }

    if (!alwaysDropdown) {
      setLocalOpenKeys(newOpenKeys);
    }

    // Call original onOpenChange prop if provided
    // Correctly refer to the component prop `onOpenChangeProp` (renamed)
    if (onOpenChangeProp) {
      onOpenChangeProp(newOpenKeys);
    }
  };

  const renderTab = (tab: MenuTab, level: number): React.ReactNode => {
    const lineHeightHorizontalMenu =
      mode === 'horizontal' ? { lineHeight: '40px' } : undefined;

    const tabId = `${tab.visualId}-side-menu-item`;
    const isSelected = localSelectedKeys.includes(tab.visualId);
    const isOpen = localOpenKeys.includes(tab.visualId);

    const tabContent = tab.tooltip ? (
      <Tooltip title={tab.tooltip}>
        <SideMenuTab
          tab={{ ...tab, selected: isSelected }}
          values={values}
          level={level}
          style={lineHeightHorizontalMenu}
        />
      </Tooltip>
    ) : (
      <SideMenuTab
        tab={{ ...tab, selected: isSelected }}
        values={values}
        level={level}
        style={lineHeightHorizontalMenu}
      />
    );

    return tab.children ? (
      <SubMenu
        key={tab.visualId}
        id={tabId}
        style={lineHeightHorizontalMenu}
        isOpen={isOpen}
        level={level}
        mode={mode}
        title={tabContent}
        onTitleClick={() => handleToggleOpen(tab.visualId)}
      >
        {isOpen &&
          tab.children?.map((childTab) => renderTab(childTab, level + 1))}
      </SubMenu>
    ) : (
      <MenuItem
        key={tab.visualId}
        id={tabId}
        style={lineHeightHorizontalMenu}
        isSelected={isSelected}
        level={level}
        mode={mode}
        onClick={(e: React.MouseEvent<HTMLElement>) =>
          handleSelect(tab.visualId, e.metaKey)
        }
      >
        {tabContent}
      </MenuItem>
    );
  };
  return (
    <MenuWrapper
      key={resetInd}
      maxheight={maxHeight}
      minheight={minHeight}
      havegradient={haveGradient.toString()}
      mode={mode}
      style={style}
      {...restForDiv}
    >
      {tabs.map((tab) => renderTab(tab, 0))}
    </MenuWrapper>
  );
};
